import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { vehicleAPI } from '../../api/vehicles';
import { Vehicle } from '../../types/entities';
import { Plus, Edit, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export const VehicleList: React.FC = () => {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const data = await vehicleAPI.getAll();
      setVehicles(data);
    } catch (error) {
      toast.error(t('vehicles.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('vehicles.deleteConfirm'))) return;

    try {
      await vehicleAPI.delete(id);
      toast.success(t('vehicles.deleteSuccess'));
      loadVehicles();
    } catch (error) {
      toast.error(t('vehicles.deleteError'));
    }
  };

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('vehicles.title')}</h1>
        <button
          onClick={() => navigate('/vehicles/new')}
          className="btn-primary flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('vehicles.addNew')}
        </button>
      </div>

      <div className="card">
        <table className="w-full">
          <thead className="border-b">
            <tr className="text-left">
              <th className="pb-3 table-header">{t('vehicles.licensePlate')}</th>
              <th className="pb-3 table-header">{t('vehicles.type')}</th>
              <th className="pb-3 table-header">{t('vehicles.capacity')}</th>
              <th className="pb-3 table-header">{t('vehicles.status')}</th>
              <th className="pb-3 table-header">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-gray-500">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              vehicles.map((vehicle) => (
                <tr key={vehicle.id} className="border-b last:border-0">
                  <td className="py-3 font-medium">{vehicle.licensePlate}</td>
                  <td className="py-3">
                    {vehicle.type ? t(`vehicles.types.${vehicle.type}`, vehicle.type) : '-'}
                  </td>
                  <td className="py-3">{vehicle.capacity ? `${vehicle.capacity} ${t('vehicles.capacityUnit')}` : '-'}</td>
                  <td className="py-3">
                    <span
                      className={`px-2 py-1 rounded text-sm ${
                        vehicle.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {vehicle.status === 'ACTIVE' ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/vehicles/${vehicle.id}/edit`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('common.edit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(vehicle.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
